import { BinaryKeyValueStore } from "socker/shared";
import { useFindexSocket } from "../useFindexSocket";
import { EAssistantEvent, type AssistantEventMap, type IActiveToolCall, type IAssistantMessage, type IAssistantMessageChunk } from "./types/message";
import { FAKE_MESSAGES } from "./mock";
import { EventSystem } from "@/utils/event/eventSystem";
import { refreshInvestments } from '~/composables/useDataRefresh';
import { useUserWorth } from '@/modules/user/hooks/useUserWorth';
import { useUserStore } from '@/stores/user';
import { useFileUpload } from '~/composables/useFileUpload';
import type { IAttachment, ISavedDocument } from 'findef';
import { pushToast } from 'components/toast/hooks';

const APP_NAME = 'assistant';

const DEBUG: boolean = false as boolean;

const TOOL_LABEL_MAP: Record<string, string> = {
  get_user_worth: 'Analyzing portfolio…',
  get_portfolio_summary: 'Loading portfolio summary…',
  get_portfolio_diversification_chart: 'Charting diversification…',
  get_portfolio_performance_chart: 'Charting performance…',
  find_investments_by_name: 'Searching investments…',
  get_investment_price_history: 'Loading price history…',
  compare_investments: 'Comparing investments…',
  get_investment_details: 'Loading investment details…',
  get_investor_updates: 'Checking investor updates…',
  get_news: 'Reading latest news…',
  query_global_assets: 'Searching assets…',
  query_global_company_profiles: 'Looking up companies…',
  send_notification: 'Sending notification…',
  create_investment: 'Creating investment…',
  import_investments_csv: 'Importing investments from CSV…',
  set_investment_parent: 'Updating investment…',
  unset_investment_parent: 'Updating investment…',
  set_user_display_currency: 'Updating currency…',
  get_current_user: 'Loading user data…',
};

const getToolLabel = (toolName: string): string => TOOL_LABEL_MAP[toolName] || 'Working…';

export type IPendingAttachment = ISavedDocument<IAttachment>;

export type IStagedAttachmentReady = {
  key: string;
  status: 'ready';
  id: string;
  name: string;
  size: number;
  mime?: string;
  previewUrl?: string;
};

export type IStagedAttachmentUploading = {
  key: string;
  status: 'uploading';
  name: string;
  size: number;
  mime?: string;
  previewUrl?: string;
};

export type IStagedAttachmentError = {
  key: string;
  status: 'error';
  name: string;
  size: number;
  mime?: string;
  previewUrl?: string;
  errorMessage: string;
  retryFile?: File;
};

export type IStagedAttachment =
  | IStagedAttachmentReady
  | IStagedAttachmentUploading
  | IStagedAttachmentError;

export const ASSISTANT_MAX_FILE_BYTES = 10 * 1024 * 1024;

export const ASSISTANT_ACCEPTED_MIME_PATTERNS: Array<RegExp> = [
  /^image\//,
  /^application\/pdf$/,
  /^text\//,
  /^application\/(json|xml|x-yaml|yaml|x-ndjson|csv)$/,
];

export const ASSISTANT_ACCEPTED_EXTENSIONS = [
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.heic',
  '.pdf',
  '.csv', '.tsv', '.txt', '.md', '.json', '.yaml', '.yml', '.log', '.xml',
];

export const isAssistantFileAccepted = (file: File): boolean => {
  const mime = (file.type || '').toLowerCase();
  if (mime && ASSISTANT_ACCEPTED_MIME_PATTERNS.some((re) => re.test(mime))) return true;
  const lower = file.name.toLowerCase();
  return ASSISTANT_ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

export type UseAssistant = {
  sendMessage: (message: string) => void;
  messages: ComputedRef<Array<IAssistantMessage>>;
  agentIsTyping: ComputedRef<boolean>;
  agentIsThinking: ComputedRef<boolean>;
  events: EventSystem<AssistantEventMap>;
  attachments: ComputedRef<Array<IStagedAttachment>>;
  attachmentsUploading: ComputedRef<boolean>;
  attachFiles: () => Promise<void>;
  attachDroppedFiles: (files: Array<File>) => Promise<void>;
  removeAttachment: (key: string) => void;
  retryAttachment: (key: string) => Promise<void>;
  clearAttachments: () => void;
}



export const useAssistant = (): UseAssistant => {
  const events = new EventSystem<AssistantEventMap>();
  
  let userMessageCount: number = 0;
  
  const socket = useFindexSocket();
  const messages = ref<Array<IAssistantMessage>>(DEBUG ? FAKE_MESSAGES : []);
  const messageStreams = ref<Map<string, IAssistantMessageChunk>>(new Map());

  const agentIsThinking = ref<boolean>(false);
  const agentIsTyping = ref<boolean>(false);
  const activeToolCalls = ref<Map<string, IActiveToolCall>>(new Map());

  const fileUpload = useFileUpload({ startTransactionsOnDialogClose: false });
  const stagedAttachments = ref<Array<IStagedAttachment>>([]);
  const blobUrls = new Map<string, string>();

  let stagedKeyCounter = 0;
  const nextKey = (): string => {
    stagedKeyCounter += 1;
    return `staged-${Date.now()}-${stagedKeyCounter}`;
  };

  const attachmentsUploading = computed(() =>
    stagedAttachments.value.some((s) => s.status === 'uploading'),
  );

  const makePreviewUrl = (file: File, key: string): string | undefined => {
    if (!file.type.startsWith('image/')) return undefined;
    const url = URL.createObjectURL(file);
    blobUrls.set(key, url);
    return url;
  };

  const replaceStaged = (key: string, next: IStagedAttachment | null) => {
    const idx = stagedAttachments.value.findIndex((s) => s.key === key);
    if (idx < 0) return;
    if (next === null) {
      stagedAttachments.value.splice(idx, 1);
    } else {
      stagedAttachments.value.splice(idx, 1, next);
    }
  };

  const validateFile = (file: File): string | null => {
    if (file.size > ASSISTANT_MAX_FILE_BYTES) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      return `${file.name} is ${mb} MB — max is 10 MB.`;
    }
    if (!isAssistantFileAccepted(file)) {
      return `${file.name} isn't a supported type. Try an image, PDF, CSV, or text file.`;
    }
    return null;
  };

  const uploadOne = async (file: File, key: string): Promise<void> => {
    try {
      const result = await fileUpload.uploadFile(file);
      if (!result) {
        replaceStaged(key, {
          key,
          status: 'error',
          name: file.name,
          size: file.size,
          mime: file.type,
          previewUrl: blobUrls.get(key),
          errorMessage: 'Upload failed.',
          retryFile: file,
        });
        return;
      }
      const saved = result as unknown as IPendingAttachment;
      replaceStaged(key, {
        key,
        status: 'ready',
        id: String(saved._id),
        name: saved.name || file.name,
        size: file.size,
        mime: saved.mime || file.type,
        previewUrl: blobUrls.get(key),
      });
    } catch (e) {
      replaceStaged(key, {
        key,
        status: 'error',
        name: file.name,
        size: file.size,
        mime: file.type,
        previewUrl: blobUrls.get(key),
        errorMessage: e instanceof Error ? e.message : 'Upload failed.',
        retryFile: file,
      });
    }
  };

  const stageFiles = async (files: Array<File>): Promise<void> => {
    if (!files || files.length <= 0) return;
    const accepted: Array<{ file: File; key: string }> = [];
    const rejections: string[] = [];
    for (const file of files) {
      const reason = validateFile(file);
      if (reason) {
        rejections.push(reason);
        continue;
      }
      const key = nextKey();
      const previewUrl = makePreviewUrl(file, key);
      stagedAttachments.value.push({
        key,
        status: 'uploading',
        name: file.name,
        size: file.size,
        mime: file.type,
        previewUrl,
      });
      accepted.push({ file, key });
    }
    if (rejections.length > 0) {
      pushToast({
        title: rejections.length === 1 ? 'File rejected' : `${rejections.length} files rejected`,
        subtitle: rejections.join(' '),
        variant: 'warning',
      });
    }
    await Promise.all(accepted.map(({ file, key }) => uploadOne(file, key)));
    fileUpload.clearAllTransactions();
  };

  const attachFiles = async (): Promise<void> => {
    const files = await new Promise<Array<File>>((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = true;
      input.accept = ASSISTANT_ACCEPTED_EXTENSIONS.join(',');
      input.onchange = () => {
        const list = input.files ? Array.from(input.files) : [];
        resolve(list);
      };
      input.oncancel = () => resolve([]);
      input.click();
    });
    await stageFiles(files);
  };

  const attachDroppedFiles = async (files: Array<File>): Promise<void> => {
    await stageFiles(files);
  };

  const removeAttachment = (key: string) => {
    const url = blobUrls.get(key);
    if (url) {
      URL.revokeObjectURL(url);
      blobUrls.delete(key);
    }
    stagedAttachments.value = stagedAttachments.value.filter((s) => s.key !== key);
  };

  const retryAttachment = async (key: string): Promise<void> => {
    const entry = stagedAttachments.value.find((s) => s.key === key);
    if (!entry || entry.status !== 'error' || !entry.retryFile) return;
    replaceStaged(key, {
      key,
      status: 'uploading',
      name: entry.name,
      size: entry.size,
      mime: entry.mime,
      previewUrl: entry.previewUrl,
    });
    await uploadOne(entry.retryFile, key);
  };

  const clearAttachments = () => {
    for (const url of blobUrls.values()) URL.revokeObjectURL(url);
    blobUrls.clear();
    stagedAttachments.value = [];
  };

  const latestToolCall = computed((): IActiveToolCall | null => {
    if (activeToolCalls.value.size <= 0) return null;
    const calls = Array.from(activeToolCalls.value.values());
    return calls.reduce((latest, call) => (!latest || call.startedAt > latest.startedAt ? call : latest), null as IActiveToolCall | null);
  });

  const mockMessage = computed((): IAssistantMessage | null => {
    const toolCall = latestToolCall.value;
    if (toolCall) {
      // Share the id with the plain thinking state so the MessageBubble keeps
      // the same v-for key across tool transitions. Otherwise Vue unmounts the
      // bubble (avatar and fadeInMsg animation included) each time the active
      // tool changes — only the shimmer label should re-animate.
      return {
        content: toolCall.label,
        id: 'agent-thinking',
        role: 'agent',
        chunk: false,
        thinking: true,
        toolCall: toolCall
      };
    }

    if (agentIsThinking.value) {
      return {
        content: '...',
        id: 'agent-thinking',
        role: 'agent',
        chunk: false,
        thinking: true
      }
    }

    if (messageStreams.value.size > 0) {
      const streams = Array.from(messageStreams.value.values());
      const text = streams.map(it => it.content).join('');

      return {
        content: text,
        id: streams[0].id,
        role: 'agent',
        chunk: false,
        stream: true
      };
    }

    return null;
  });

  const outMessages = computed((): Array<IAssistantMessage> => {
    const msgs = [...messages.value];
    const mock = mockMessage.value;
    if (mock) {
      msgs.push(mock);
    }
    return msgs.filter(it => (!!it.content && it.content.trim().length > 0) || it.chunk || it.thinking || it.stream);
  });

  
  const sendMessage = (message: string) => {
    agentIsThinking.value = true;

    const ready = stagedAttachments.value.filter(
      (s): s is IStagedAttachmentReady => s.status === 'ready',
    );
    const messageAttachments = ready.map((a) => ({
      id: a.id,
      name: a.name,
      mime: a.mime,
      previewUrl: a.previewUrl,
    }));
    const attachmentIds = ready.map((a) => a.id);

    socket.send(BinaryKeyValueStore.fromJS({
      action: 'SEND_MESSAGE',
      app: APP_NAME,
      message: message,
      attachmentIds: attachmentIds,
    }));
    messages.value.push({
      id: `user-msg-${userMessageCount}`,
      content: message,
      role: 'user',
      attachments: messageAttachments.length > 0 ? messageAttachments : undefined,
    });
    userMessageCount += 1;
    // Drop the staged list but keep blob URLs alive — the bubble references them.
    // They'll be released next time the modal mounts (composable re-init) or via
    // the browser when the page unloads.
    stagedAttachments.value = [];

    events.emit({
      eventType: EAssistantEvent.OUTGOING_MESSAGE
    });
  }

  const onMessageStream = (msg: BinaryKeyValueStore) => {
    agentIsThinking.value = false;
    agentIsTyping.value = true;
    if (activeToolCalls.value.size > 0) {
      activeToolCalls.value = new Map();
    }

    const message = msg.getString('message');
    if (!message) return;
    const itemId = msg.getString('itemId');
    if (typeof itemId !== 'string') return;
    const index = msg.getInt32('index');
    if (typeof index !== 'number') return;

    messageStreams.value.set(itemId, {
      content: message,
      id: itemId,
      index: index
    })

    events.emit({
      eventType: EAssistantEvent.INCOMING_MESSAGE_CHUNK
    });
  }

  const onResponseDone = (msg: BinaryKeyValueStore) => {
    agentIsTyping.value = false;
    agentIsThinking.value = false;
    activeToolCalls.value = new Map();

    const id = msg.getString('id');
    //const responseId = msg.getString('responseId');
    if (typeof id !== 'string') return;
    const content = msg.getString('message') || '';
    messageStreams.value.delete(id);
    messages.value.push({
      id: id,
      content: content,
      role: 'agent'
    })
  }

  const onError = (msg: BinaryKeyValueStore) => {
    const message = msg.getString('message') || 'Unknown error';
    messages.value.push({
      id: `error-${messages.value.length}`,
      content: message,
      role: 'agent'
    });
    messageStreams.value.clear();
    activeToolCalls.value = new Map();
    agentIsThinking.value = false;
    agentIsTyping.value = false;
  }

  const onOutputGardrail = (msg: BinaryKeyValueStore) => {
    // The offending assistant text is still live in messageStreams (it never
    // reached RESPONSE_DONE because the guardrail tripped). Drop it, then
    // append the correction as a completed message.
    messageStreams.value.clear();
    const message = msg.getString('message');
    if (message && message.length > 0) {
      messages.value.push({
        id: `output-guardrail-${messages.value.length}`,
        content: message,
        role: 'agent'
      });
    }
    activeToolCalls.value = new Map();
    agentIsThinking.value = false;
    agentIsTyping.value = false;
  }

  const onInputGuardrail = (msg: BinaryKeyValueStore) => {
    // Input guardrail aborts before any chunks are streamed, so messageStreams
    // is already empty — just append the refusal.
    const message = msg.getString('message');
    if (message && message.length > 0) {
      messages.value.push({
        id: `input-guardrail-${messages.value.length}`,
        content: message,
        role: 'agent'
      });
    }
    activeToolCalls.value = new Map();
    agentIsThinking.value = false;
    agentIsTyping.value = false;
  }

  const onLoadingEvent = (_msg: BinaryKeyValueStore) => {
    agentIsThinking.value = true;
    agentIsTyping.value = true;
  }

  const onToolCallStart = (msg: BinaryKeyValueStore) => {
    const callId = msg.getString('callId');
    const toolName = msg.getString('toolName');
    if (typeof callId !== 'string' || typeof toolName !== 'string') return;
    const next = new Map(activeToolCalls.value);
    next.set(callId, {
      callId,
      toolName,
      label: getToolLabel(toolName),
      startedAt: Date.now()
    });
    activeToolCalls.value = next;
  }

  const onToolCallComplete = (_msg: BinaryKeyValueStore) => {
    // Intentionally do not remove the entry here. If the tool finishes before
    // the model emits text, removing would drop the pill back to the plain
    // thinking animation for the brief gap before streaming begins. Entries
    // are cleared in onMessageStream / onResponseDone / onError instead, so
    // the pill stays visible throughout the whole "agent is working" window
    // (and if a second tool runs, latestToolCall picks the newer startedAt).
  }

  const onDataChanged = (msg: BinaryKeyValueStore) => {
    const entities = msg.getStringArray('entities');
    if (!entities || entities.length === 0) return;

    for (const entity of entities) {
      switch (entity) {
        case 'investments': {
          refreshInvestments();
          break;
        }
        case 'user_worth': {
          useUserWorth().refresh();
          break;
        }
        case 'user': {
          const userStore = useUserStore();
          userStore.refreshUser();
          break;
        }
      }
    }
  }

  socket.onReady(() => {
    socket.send(BinaryKeyValueStore.fromJS({
      action: 'INIT',
      app: APP_NAME 
    }));

    socket.onMessage((msg) => {
      const app = msg.getString("app");
      if (app !== APP_NAME) return;
      const action = msg.getString("action");
      if (!action) return;

      switch (action) {
        case 'MESSAGE_CHUNK': {
          //onMessageChunk(msg);
        }; break;
        case 'MESSAGE_STREAM': {
          onMessageStream(msg);
        }; break;
        case 'RESPONSE_DONE': {
          onResponseDone(msg);
        }; break;
        case 'ERROR': {
          onError(msg);
        }; break;
        case 'OUTPUT_GUARDRAIL': {
          onOutputGardrail(msg);
        }; break;
        case 'INPUT_GUARDRAIL': {
          onInputGuardrail(msg);
        }; break;
        case 'LOADING': {
          onLoadingEvent(msg);
        }; break;
        case 'TOOL_CALL_START': {
          onToolCallStart(msg);
        }; break;
        case 'TOOL_CALL_COMPLETE': {
          onToolCallComplete(msg);
        }; break;
        case 'DATA_CHANGED': {
          onDataChanged(msg);
        }; break;
      }
    })
  });

  return {
    sendMessage: sendMessage,
    messages: outMessages,
    agentIsTyping: computed(() => agentIsTyping.value),
    agentIsThinking: computed(() => agentIsThinking.value),
    events: events,
    attachments: computed(() => stagedAttachments.value),
    attachmentsUploading: attachmentsUploading,
    attachFiles: attachFiles,
    attachDroppedFiles: attachDroppedFiles,
    removeAttachment: removeAttachment,
    retryAttachment: retryAttachment,
    clearAttachments: clearAttachments,
  };
}
